/** Shared message protocol: Admin 2.0 playground ↔ SPA iframe */

export const ADMIN_PREVIEW_SOURCE = 'dg-admin' as const
export const ADMIN_PREVIEW_MESSAGE_TYPE = 'dg-admin-preview' as const

export const SPA_PREVIEW_SOURCE = 'dg-spa' as const
export const SPA_PREVIEW_MESSAGE_TYPE = 'dg-spa-preview' as const

export type AdminPreviewAction =
  | 'autofill'
  | 'reset'
  | 'gotoQuestion'
  | 'gotoStep'
  | 'capture'
  | 'capture_illustration'
  | 'navigate'

export type AdminPreviewPayload = {
  mode?: 'preset' | 'random'
  presetId?: string
  scope?: 'from_zero' | 'remaining_only'
  questionId?: string
  questionKey?: string
  step?: string
  page?: string
}

export type AdminPreviewMessage = {
  source: typeof ADMIN_PREVIEW_SOURCE
  type: typeof ADMIN_PREVIEW_MESSAGE_TYPE
  action: AdminPreviewAction
  payload?: AdminPreviewPayload
}

export type SpaPreviewAction = 'ready' | 'autofill_done' | 'capture_result' | 'page_changed' | 'error'

export type SpaPreviewPayload = {
  dataUrl?: string
  page?: string
  message?: string
  width?: number
  height?: number
}

export type SpaPreviewMessage = {
  source: typeof SPA_PREVIEW_SOURCE
  type: typeof SPA_PREVIEW_MESSAGE_TYPE
  action: SpaPreviewAction
  payload?: SpaPreviewPayload
}

export function isAdminPreviewMessage(data: unknown): data is AdminPreviewMessage {
  if (!data || typeof data !== 'object') return false
  const msg = data as Record<string, unknown>
  return msg.source === ADMIN_PREVIEW_SOURCE && msg.type === ADMIN_PREVIEW_MESSAGE_TYPE
}

export function isSpaPreviewMessage(data: unknown): data is SpaPreviewMessage {
  if (!data || typeof data !== 'object') return false
  const msg = data as Record<string, unknown>
  return msg.source === SPA_PREVIEW_SOURCE && msg.type === SPA_PREVIEW_MESSAGE_TYPE
}

export function buildAdminPreviewMessage(
  action: AdminPreviewAction,
  payload?: AdminPreviewPayload,
): AdminPreviewMessage {
  return {
    source: ADMIN_PREVIEW_SOURCE,
    type: ADMIN_PREVIEW_MESSAGE_TYPE,
    action,
    payload,
  }
}

export function buildSpaPreviewMessage(
  action: SpaPreviewAction,
  payload?: SpaPreviewPayload,
): SpaPreviewMessage {
  return {
    source: SPA_PREVIEW_SOURCE,
    type: SPA_PREVIEW_MESSAGE_TYPE,
    action,
    payload,
  }
}

/** Origins allowed to control the SPA iframe (admin Vite + same host). */
export function isAllowedAdminPreviewOrigin(origin: string): boolean {
  try {
    const url = new URL(origin)
    if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') return true
    if (typeof window !== 'undefined' && url.hostname === window.location.hostname) return true
  } catch {
    return false
  }
  return false
}

export function postSpaPreviewToParent(
  action: SpaPreviewAction,
  payload?: SpaPreviewPayload,
): void {
  if (typeof window === 'undefined' || window.parent === window) return
  window.parent.postMessage(buildSpaPreviewMessage(action, payload), '*')
}
